import actionTypes from 'constants/actionTypes'
import { successGraphQuery, failGraphQuery, setGraphQueryStatus } from 'actions/graphQuery'
import { addShape, removeShape } from 'actions/shapes'
import { changePrompt } from 'actions/prompt'
import { selectNeighborhoodDeep } from 'selectors/graph'
import selectPrompt from 'selectors/prompt'
import { getKey } from 'api/apiKey'
import { createId } from 'lib/id'
import { defaultListSettings } from 'types/shapes'

type RootState = import('types/state').RootState
type GraphNodeMap = import('types/domain').GraphNodeMap
type GraphEdgeMap = import('types/domain').GraphEdgeMap
type NeighborhoodResult = import('types/domain').NeighborhoodResult

const logging = {
  llmPrompts: false,
  llmRawResponses: false,
  llmTextResponses: false,
  workflowSteps: true,
}

type MiddlewareStore = {
  getState: () => RootState
  dispatch: (action: unknown) => unknown
}

type NextFn = (action: { type: string }) => unknown

type GraphQueryAction = {
  type: string
}

type ContentBlock = {
  type: 'text'
  text: string
  cache_control?: { type: 'ephemeral' }
}

type LlmResponse = {
  content?: Array<{ type?: string; text?: string }>
  usage?: Record<string, number>
}

type AnswerEntity = {
  id: string
  type: 'node' | 'edge'
}

const GRAPH_QUERY_REQUEST = String(actionTypes.GRAPH_QUERY.REQUEST)
const GRAPH_QUERY_CANCEL = String(actionTypes.GRAPH_QUERY.CANCEL)

const selectNeighborhoodDeepForState = (state: RootState): NeighborhoodResult => {
  return selectNeighborhoodDeep(state) as NeighborhoodResult
}

const selectPromptForState = (state: RootState): string => {
  const promptState = {
    prompt: String((state as { prompt?: unknown }).prompt || ''),
  }
  return String(selectPrompt(promptState) || '')
}

const OUTPUT_SCHEMA = {
  type: 'object',
  properties: {
    answer: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          type: { type: 'string', enum: ['node', 'edge'] },
        },
        required: ['id', 'type'],
        additionalProperties: false,
      },
    },
  },
  required: ['answer'],
  additionalProperties: false,
}

const SYSTEM_PROMPT = [
  'You are given a linguistic knowledge graph and a question.',
  'The graph encodes natural language using the following conventions:',
  '- Noun and verb words are nodes.',
  '- Adjectives and adverbs are self-loop edges on their respective noun/verb nodes.',
  '- Grammatical relations (subject, object, indirect object, oblique, etc.) are edges between verb and noun nodes. Subject and object edges have empty content — their meaning is carried by their kind.',
  '- Verb nodes are connected to their grammatical relations (subject, object, etc.) via these edges. A verb\'s meaning is inseparable from its grammatical relations — e.g. a verb with a specific subject and object means specifically that subject performing that action on that object, not the verb in general.',
  'Find nodes and edges from the graph whose content can be arranged into a sentence that answers (at least partially) the question.',
  'Return ONLY entities that exist in the provided graph. Do NOT invent new entities.',
  'Return them in sentence reading order — the order their content should appear when read as a natural language sentence.',
  'Be sure that the words are placed in the order that is most idiomatic for english.',
  'Prefer answers that form complete propositions (subject + verb + object) over bare noun phrases or fragments.',
  'If the question mentions concepts that correspond to nodes in the graph, prefer to include those nodes in the answer to anchor it to the question.',
  'The answer nodes must form a connected subgraph: you must be able to reach every answer node from every other answer node by following edges in the graph, passing only through other answer nodes. Never stitch together nodes from disconnected parts of the graph.',
  'Do NOT include entities with empty content in the answer — they contribute no readable text. Use them only to understand the graph structure.',
  'If the graph does not contain enough information to answer the question, return the closest partial answer you can assemble from existing entities.',
  'If no entities are relevant at all, return an empty answer array.',
].join('\n')

class GraphQueryHandler {
  store: MiddlewareStore
  apiKey: string
  _abortController: AbortController
  _isCancelled: boolean
  _shapeId: string
  _usage: { input_tokens: number; output_tokens: number; cache_creation_input_tokens: number; cache_read_input_tokens: number }

  constructor(store: MiddlewareStore) {
    this.store = store
    this.apiKey = getKey() || ''
    this._abortController = new AbortController()
    this._isCancelled = false
    this._shapeId = createId()
    this._usage = { input_tokens: 0, output_tokens: 0, cache_creation_input_tokens: 0, cache_read_input_tokens: 0 }
  }

  cancel() {
    this._isCancelled = true
    this._abortController.abort()
  }

  _accumulateUsage(usage: Record<string, number>) {
    this._usage.input_tokens += usage.input_tokens || 0
    this._usage.output_tokens += usage.output_tokens || 0
    this._usage.cache_creation_input_tokens += usage.cache_creation_input_tokens || 0
    this._usage.cache_read_input_tokens += usage.cache_read_input_tokens || 0
  }

  _serializeNode(node: { id: string; data: { content: string; kind: string } }): string {
    const compact: Record<string, string> = { c: node.data.content || '' }
    compact.i = node.id
    if (node.data.kind != null) {
      compact.k = node.data.kind
    }
    return JSON.stringify(compact)
  }

  _serializeEdge(edge: { id: string; data: { content: string; from: string; to: string; directionality: string; kind: string } }): string {
    const compact: Record<string, string> = { c: edge.data.content || '' }
    compact.i = edge.id
    compact.f = edge.data.from
    compact.t = edge.data.to || ''
    compact.d = edge.data.directionality === 'DIRECTED' ? 'D' : 'U'
    if (edge.data.kind != null) {
      compact.k = edge.data.kind
    }
    return JSON.stringify(compact)
  }

  _buildUserPrompt(nodes: GraphNodeMap, edges: GraphEdgeMap, question: string): string {
    const nodeLines = Object.values(nodes)
      .filter((n) => !n.deleted)
      .map((n) => this._serializeNode(n as { id: string; data: { content: string; kind: string } }))
    const edgeLines = Object.values(edges)
      .filter((e) => !e.deleted)
      .map((e) => this._serializeEdge(e as { id: string; data: { content: string; from: string; to: string; directionality: string; kind: string } }))

    return [
      'Node format: {"c": "<content>", "i": "<id>", "k": "<kind>"}',
      'Edge format: {"c": "<content>", "i": "<id>", "f": "<from node id>", "t": "<to node id>", "d": "D|U", "k": "<kind>"}',
      '',
      'Nodes:',
      ...nodeLines,
      '',
      'Edges:',
      ...edgeLines,
      '',
      `Question: ${question}`,
    ].join('\n')
  }

  _fetchLlm(systemPrompt: string, userPrompt: string): Promise<string> {
    if (logging.llmPrompts) {
      console.log('---SYSTEM PROMPT---\n', systemPrompt, '\n---USER PROMPT---\n', userPrompt, '\n-----------------------')
    }
    const system: ContentBlock[] = [
      { type: 'text' as const, text: systemPrompt, cache_control: { type: 'ephemeral' as const } },
    ]
    const body = {
      system,
      messages: [{ role: 'user', content: userPrompt }],
      model: 'claude-opus-4-6',
      max_tokens: 4096,
      output_config: { format: { type: 'json_schema', schema: OUTPUT_SCHEMA } },
    }
    const headers: Record<string, string> = {
      'x-api-key': this.apiKey,
      'Content-Type': 'application/json',
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    }
    const req: RequestInit = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: this._abortController.signal,
    }

    const doFetch = (retriesLeft: number): Promise<Response> => {
      return fetch('https://api.anthropic.com/v1/messages', req).then((res) => {
        if (res.status === 429 && retriesLeft > 0) {
          const retryAfter = parseInt(res.headers.get('retry-after') || '', 10)
          const waitSeconds = retryAfter > 0 && retryAfter <= 300 ? retryAfter : 60
          console.log(`⏳ Rate limited — waiting ${waitSeconds}s before retry (${retriesLeft} retries left)`)
          return new Promise<Response>((resolve) => setTimeout(resolve, waitSeconds * 1000)).then(() => doFetch(retriesLeft - 1))
        }
        if (res.status === 529 && retriesLeft > 0) {
          console.log(`⏳ API overloaded (529) — waiting 60s before retry (${retriesLeft} retries left)`)
          return new Promise<Response>((resolve) => setTimeout(resolve, 60000)).then(() => doFetch(retriesLeft - 1))
        }
        return res
      })
    }

    return doFetch(3)
      .then((res) => {
        if (!res.ok) {
          return res.text().then((t) => {
            throw new Error(`HTTP ${res.status}: ${t}`)
          })
        }
        return res.json()
      })
      .then((json: LlmResponse) => {
        if (logging.llmRawResponses) {
          console.log('---RAW RESPONSE---\n', json, '\n-----------------------')
        }
        const usage = json.usage
        if (usage) {
          this._accumulateUsage(usage)
        }
        const text = json.content?.find((c) => c.type === 'text')?.text || '{}'
        if (logging.llmTextResponses) {
          console.log('---TEXT RESPONSE---\n', text, '\n-----------------------')
        }
        return text
      })
  }

  run(): Promise<void> {
    this.store.dispatch(setGraphQueryStatus('Ask'))
    if (logging.workflowSteps) {
      console.log('---GRAPH QUERY---')
    }
    const state = this.store.getState()
    const question = selectPromptForState(state)
    if (!question) {
      console.warn('Graph query: no question provided')
      this.store.dispatch(failGraphQuery())
      return Promise.resolve()
    }

    const neighborhood = selectNeighborhoodDeepForState(state)
    const nodes = neighborhood.nodes
    const edges = neighborhood.edges
    if (logging.workflowSteps) {
      console.log(`Graph query: ${Object.keys(nodes).length} nodes, ${Object.keys(edges).length} edges in neighborhood`)
    }

    const userPrompt = this._buildUserPrompt(nodes, edges, question)

    return this._fetchLlm(SYSTEM_PROMPT, userPrompt)
      .then((text) => {
        if (this._isCancelled) throw new Error('Cancelled')

        const parsed: { answer?: AnswerEntity[] } = JSON.parse(text)
        const answerEntities = parsed.answer || []

        if (answerEntities.length === 0) {
          console.log('Graph query: no matching entities found in the graph')
          this.store.dispatch(successGraphQuery())
          return
        }

        // Resolve entities and build sentence
        const validEntities: AnswerEntity[] = []
        const sentenceParts: string[] = []
        const annotatedParts: string[] = []

        for (const ref of answerEntities) {
          if (ref.type === 'node' && nodes[ref.id]) {
            const content = (nodes[ref.id] as { data: { content: string } }).data.content
            if (!content?.trim()) continue
            validEntities.push(ref)
            sentenceParts.push(content)
            annotatedParts.push(`${content} (node:${ref.id})`)
          } else if (ref.type === 'edge' && edges[ref.id]) {
            const content = (edges[ref.id] as { data: { content: string } }).data.content
            if (!content?.trim()) continue
            validEntities.push(ref)
            sentenceParts.push(content)
            annotatedParts.push(`${content} (edge:${ref.id})`)
          }
        }

        console.log(sentenceParts.join(' '))
        console.log(annotatedParts.join(' '))

        this.store.dispatch(addShape({
          id: this._shapeId,
          shapeType: 'list',
          entityIds: validEntities.map((e) => e.id),
          settings: defaultListSettings,
          layout: true,
        }))
        this.store.dispatch(successGraphQuery())
        this.store.dispatch(changePrompt(''))
      })
      .catch((err: unknown) => {
        if (this._isCancelled) return
        console.error('Graph query error:', err)
        this.store.dispatch(failGraphQuery())
      })
      .then(() => {
        const u = this._usage
        console.log(
          `📊 Token usage — input: ${u.input_tokens}, cache_read: ${u.cache_read_input_tokens}, cache_create: ${u.cache_creation_input_tokens}, output: ${u.output_tokens}`
        )
      })
  }
}

let currentHandler: GraphQueryHandler | null = null
let currentShapeId: string | null = null

export default (store: MiddlewareStore) => (next: NextFn) => (action: GraphQueryAction) => {
  if (action.type === GRAPH_QUERY_REQUEST) {
    // Remove previous shape if a new query starts
    if (currentShapeId) {
      store.dispatch(removeShape(currentShapeId) as unknown as GraphQueryAction)
      currentShapeId = null
    }
    const handler = new GraphQueryHandler(store)
    currentHandler = handler
    currentShapeId = handler._shapeId
    handler.run().then(() => {
      console.log('-------FINISHED GRAPH QUERY--------')
      currentHandler = null
    })
  }
  if (action.type === GRAPH_QUERY_CANCEL) {
    if (currentHandler) {
      currentHandler.cancel()
      currentHandler = null
    }
    if (currentShapeId) {
      store.dispatch(removeShape(currentShapeId) as unknown as GraphQueryAction)
      currentShapeId = null
    }
  }
  return next(action)
}
